import { useMemo } from 'react';

/**
 * Hook para convertir errores de Firebase en mensajes amigables para el usuario
 */
export const useFirebaseError = () => {
  const getErrorMessage = useMemo(() => {
    return (error) => {
      if (!error) return '';

      // Si es string, retornarlo directamente
      if (typeof error === 'string') return error;

      // Errores específicos de Firebase Auth
      const firebaseErrors = {
        // Errores de autenticación
        'auth/user-not-found': 'Usuario no encontrado. Verifica tu email.',
        'auth/wrong-password': 'Contraseña incorrecta.',
        'auth/invalid-email': 'Email inválido.',
        'auth/user-disabled': 'Esta cuenta ha sido deshabilitada.',
        'auth/email-already-in-use': 'Este email ya está registrado.',
        'auth/weak-password': 'La contraseña es muy débil.',
        'auth/operation-not-allowed': 'Operación no permitida.',
        'auth/account-exists-with-different-credential': 'Ya existe una cuenta con este email usando un método diferente.',
        'auth/invalid-credential': 'Credenciales inválidas.',
        'auth/credential-already-in-use': 'Estas credenciales ya están en uso.',
        'auth/invalid-verification-code': 'Código de verificación inválido.',
        'auth/invalid-verification-id': 'ID de verificación inválido.',
        'auth/missing-verification-code': 'Falta el código de verificación.',
        'auth/missing-verification-id': 'Falta el ID de verificación.',
        'auth/code-expired': 'El código de verificación ha expirado.',
        'auth/invalid-phone-number': 'Número de teléfono inválido.',
        'auth/missing-phone-number': 'Falta el número de teléfono.',
        'auth/quota-exceeded': 'Cuota excedida. Intenta más tarde.',
        'auth/user-token-expired': 'Tu sesión ha expirado. Inicia sesión nuevamente.',
        'auth/network-request-failed': 'Error de conexión. Verifica tu internet.',
        'auth/internal-error': 'Error interno. Intenta más tarde.',
        'auth/too-many-requests': 'Demasiados intentos. Espera un momento.',
        'auth/timeout': 'La operación tardó demasiado. Intenta nuevamente.',

        // Errores de Cloud Functions
        'functions/cancelled': 'Operación cancelada.',
        'functions/unknown': 'Error desconocido del servidor.',
        'functions/invalid-argument': 'Datos inválidos enviados al servidor.',
        'functions/deadline-exceeded': 'Tiempo de espera agotado.',
        'functions/not-found': 'Función no encontrada.',
        'functions/already-exists': 'El recurso ya existe.',
        'functions/permission-denied': 'Permisos insuficientes.',
        'functions/resource-exhausted': 'Recursos agotados. Intenta más tarde.',
        'functions/failed-precondition': 'Precondición fallida.',
        'functions/aborted': 'Operación abortada.',
        'functions/out-of-range': 'Valor fuera de rango.',
        'functions/unimplemented': 'Función no implementada.',
        'functions/internal': 'Error interno del servidor.',
        'functions/unavailable': 'Servicio no disponible.',
        'functions/data-loss': 'Pérdida de datos.',
        'functions/unauthenticated': 'Usuario no autenticado.',

        // Errores de Firestore
        'firestore/permission-denied': 'Permisos insuficientes para esta operación.',
        'firestore/not-found': 'Documento no encontrado.',
        'firestore/already-exists': 'El documento ya existe.',
        'firestore/resource-exhausted': 'Cuota de Firestore excedida.',
        'firestore/failed-precondition': 'Operación no válida en el estado actual.',
        'firestore/aborted': 'Operación abortada por conflicto.',
        'firestore/out-of-range': 'Valor fuera del rango permitido.',
        'firestore/unimplemented': 'Operación no implementada.',
        'firestore/internal': 'Error interno de Firestore.',
        'firestore/unavailable': 'Firestore no disponible temporalmente.',
        'firestore/data-loss': 'Pérdida de datos irrecuperable.',
        'firestore/deadline-exceeded': 'Tiempo de espera de Firestore agotado.',
        'firestore/cancelled': 'Operación de Firestore cancelada.'
      };

      // Buscar error específico
      const errorCode = error.code || error.message;
      const friendlyMessage = firebaseErrors[errorCode];

      if (friendlyMessage) {
        return friendlyMessage;
      }

      // Fallback para errores no mapeados
      if (error.message) {
        return error.message;
      }

      return 'Ha ocurrido un error inesperado. Intenta nuevamente.';
    };
  }, []);

  return { getErrorMessage };
};

export default useFirebaseError;

